import { Carto, extentMax } from './carte-config.js';
import { Map, View } from 'ol';
import { defaults as defaultControls } from 'ol/control';
import GeoJSON from 'ol/format/GeoJSON';
import WFS from 'ol/format/WFS.js';
import GML from 'ol/format/GML.js';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import TileLayer from 'ol/layer/WebGLTile';
import { bbox } from 'ol/loadingstrategy';
import Projection from 'ol/proj/Projection';
import { Cluster, ImageWMS, TileWMS, Vector as VectorSource } from 'ol/source';
import { addParamsToUrl } from '../../utils/utils.js';
import {
  getClusterStyleFunction,
  getDefaultStyle,
  createStyleForSingleFeature,
} from './carte-style.js';
import { AutourDeMoiControl } from './autour-de-moi-control.js';
import { LayerSwitcherControl } from './layer-switcher-control.js';
import { initStyles } from './carte-style-builder.js';
import { RecentrerVueControl } from './recentrer-vue-control.js';
import { VectorSourceEvent } from 'ol/source/Vector.js';

function initMapForDossier(target, options, viewOptions = {}) {
  const layers = createBaseLayers();
  layers.push(createFeatureLayer(options));
  return init(target, options, layers, viewOptions);
}

function initMapForRecherche(target, options, viewOptions = {}) {
  const layers = createBaseLayers();
  layers.push(createClusteredFeaturesLayer(options));
  return init(target, options, layers, viewOptions);
}

function initMapForDossierIllustration(target, options) {
  initStyles();

  const map = init(target, options, []);

  const couchesReferentiels = (Carto.carte.referentiels ?? []).map(
    (couche) => ({
      ...(Carto.referentiels[couche.coucheReferentielId] ?? {}),
      ...couche,
    }),
  );
  const wmsCouches = couchesReferentiels.filter(
    (couche) => couche.typeCouche === 'WMS',
  );
  const wfsCouches = couchesReferentiels.filter(
    (couche) => couche.typeCouche === 'WFS',
  );
  createBaseLayers()
    .concat(createReferentielsDossierIllustrationLayers(wmsCouches, map))
    .concat(createReferentielsDossierIllustrationLayers(wfsCouches, map))
    .concat(
      createFeatureDossierIllustrationLayers({
        ...(options ?? {}),
        indexOffset: wfsCouches.length,
      }),
    )
    .forEach((layer) => map.addLayer(layer));

  if (Carto.carte.centerX && Carto.carte.centerY) {
    map.getView().setCenter([Carto.carte.centerX, Carto.carte.centerY]);

    map.getView().setZoom(Carto.carte.zoom ?? 2);
  }

  return map;
}

function init(target, options, layers, viewOptions = {}) {
  const map = new Map({
    controls: getControls(options),
    target,
    layers,
    view: new View({
      extent: extentMax,
      showFullExtent: true,
      projection: new Projection({
        code: Carto.config.options.projection,
        units: Carto.config.options.units,
      }),
      minZoom: Carto.config.options.minZoom,
      maxZoom: Carto.config.options.maxZoom,
      ...viewOptions,
    }),
  });
  registerMapBehaviour(map, options);
  map.getView().fit(extentMax);
  return map;
}

function getControls(options) {
  let controls = defaultControls();
  if (options.controls?.autourDeMoi) {
    controls = controls.extend([new AutourDeMoiControl()]);
  }
  if (options.controls?.extentRecentrerVue) {
    controls.push(
      new RecentrerVueControl({
        extent: options.controls.extentRecentrerVue,
      }),
    );
  }
  controls.push(new LayerSwitcherControl());
  return controls;
}

function registerMapBehaviour(map, options) {
  if (!options.onFeatureClicked) {
    return;
  }
  map.on('click', function (evt) {
    const feature = map.forEachFeatureAtPixel(evt.pixel, function (feature) {
      return feature;
    });
    if (!feature) {
      return;
    }
    options.onFeatureClicked(feature);
  });
}

function createBaseLayers() {
  const fonds = [];
  Carto.config.fonds.forEach((fond) => {
    const layer = createBaseLayer(fond);
    if (fond.defaut) {
      fonds.unshift(layer);
    } else {
      fonds.push(layer);
    }
  });
  return fonds;
}

function requireTuilage(definition) {
  return !definition.singleTile || definition.tuilage;
}

function createBaseLayer(definition, estFondDePlan = true, zIndex = 0) {
  const tuilage = requireTuilage(definition);
  const sourceConf = {
    url: urlProxyfier({
      url: definition.url.replace('?', '&'),
    }),
    params: {
      LAYERS: definition.couches || definition.couche,
    },
    attributions: definition.attribution,
  };

  const layerConf = {
    visible: Boolean(definition.visible || definition.defaut),
    opacity: definition.opacity || 1,
    source: tuilage ? new TileWMS(sourceConf) : new ImageWMS(sourceConf),
    zIndex,
    properties: {
      id: definition.id,
      titre: definition.titre,
      estCoucheMetier: !estFondDePlan,
      estFondDePlan,
    },
  };

  return tuilage ? new TileLayer(layerConf) : new ImageLayer(layerConf);
}

function createWFSLayer(definition, map, index) {
  const currentStyle = Carto.styles.find(
    (style) => definition.currentStyleId === style.id,
  );

  const source = new VectorSource({
    features: [],
    attributions: definition.attribution,
  });

  const layer = new VectorLayer({
    visible: definition.visible || definition.defaut,
    opacity: definition.opacity || 1,
    source,
    style: currentStyle?.olStyle ?? getDefaultStyle(index),
    zIndex: 2,
    properties: {
      id: definition.id,
      titre: definition.titre,
      estCoucheMetier: true,
      stylesId: definition.stylesId,
      currentStyleId: definition.currentStyleId,
      defaultStyle: getDefaultStyle(index),
      // styleIndex ?
    },
  });

  const replaceFeatures = (features) => {
    source.clear();
    source.addFeatures(features);
    source.dispatchEvent(
      new VectorSourceEvent('featuresloadend', null, features),
    );
  };

  let oldZoom;
  const reload = async () => {
    const currentZoom = map.getView().getZoom();
    if (currentZoom !== null) {
      if (definition.seuilmin !== null && definition.seuilmin > currentZoom) {
        oldZoom = currentZoom;
        replaceFeatures([]);
        return;
      }
      if (definition.seuilmax !== null && definition.maxZoom < currentZoom) {
        oldZoom = currentZoom;
        replaceFeatures([]);
        return;
      }
    } else {
      replaceFeatures([]);
      return;
    }

    if (currentZoom !== oldZoom) {
      oldZoom = currentZoom;

      const features = await getFeaturesFromWFS(definition, map);
      replaceFeatures(features);
    }
  };

  const WAITING_TIME = 300;
  let timeoutId;
  const debouncedReload = () => {
    return new Promise((resolve, reject) => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(() => {
        reload().then(resolve, reject);
      }, WAITING_TIME);
    });
  };
  map.addEventListener('moveend', debouncedReload);

  debouncedReload();

  return layer;
}

async function getFeaturesFromWFS(definition, map) {
  const wfsDefinition = {
    featureTypes: [definition.couche],
    featureNS: definition.namespace,
    featurePrefix: 'feature',
    geometryName: definition.geometryName,
    outputFormat: 'gml3', // Seulement gml3 supporté pour le moment
    bbox: map.getView().calculateExtent(map.getSize()),
    srsName: map.getView().getProjection().getCode(),
  };

  const url = urlProxyfier({
    url: definition.url.replace('?', '&'),
  });

  const response = await fetch(url, {
    method: 'POST',
    body: new XMLSerializer().serializeToString(
      new WFS().writeGetFeature(wfsDefinition),
    ),
  });
  const output = await response.text();
  const features = new GML().readFeatures(output);
  return features ?? [];
}

function createClusteredFeaturesLayer(options) {
  // Définition de la source des dossiers
  const source = new VectorSource({
    format: new GeoJSON({
      dataProjection: Carto.config.projectionProduction,
      featureProjection: Carto.config.options.projection,
    }),
    strategy: bbox,
  });

  let clusterSource = new Cluster({
    minDistance: Carto.config.groupingDistance,
    distance: Carto.config.groupingDistance,
    source,
    properties: {
      nom: 'dossiersCluster',
    },
  });

  let dig = (f, ids) => {
    const features = f.get('features');
    if (features) {
      features.forEach((f) => {
        dig(f, ids);
      });
    } else {
      ids.push(f.get('id'));
    }
  };
  clusterSource.on('addfeature', (e) => {
    const ids = [];
    dig(e.feature, ids);
    ids.sort();
    e.feature.set('hashcode', hashCode(ids.join('')));
  });
  // Définition de la couche principale des dossiers
  const layer = new VectorLayer({
    // Un cluster est une source à part entière, elle-même alimentée par une autre source.
    source: clusterSource,
    style: getClusterStyleFunction(options),
    zIndex: 3,
    properties: {
      nom: 'dossiersLayer',
    },
  });

  return layer;
}

function createFeatureLayer(options) {
  // Définition de la source des dossiers
  const source = new VectorSource({
    format: new GeoJSON({
      dataProjection: Carto.config.projectionProduction,
      featureProjection: Carto.config.options.projection,
    }),
    strategy: bbox,
  });

  // Définition de la couche principale des dossiers
  const layer = new VectorLayer({
    source: source,
    style: (feature) => createStyleForSingleFeature(feature, options),
    zIndex: 3,
    properties: {
      nom: 'dossiersLayer',
    },
  });

  return layer;
}

function createFeatureDossierIllustrationLayers(options) {
  return Carto.carte.couchesMetier.map((couche, index) =>
    createFeatureDossierIllustrationLayer(couche, index, options),
  );
}

function createReferentielsDossierIllustrationLayers(couches, map) {
  return couches.map((couche, index) => {
    if (couche.typeCouche === 'WMS') {
      return createBaseLayer(couche, false, 1);
    }
    return createWFSLayer(couche, map, index);
  });
}

function createFeatureDossierIllustrationLayer(couche, index, options) {
  const source = new VectorSource({
    url: Carto.couchesUrl[index],
    format: new GeoJSON({
      dataProjection: Carto.config.projectionProduction,
      featureProjection: Carto.config.options.projection,
    }),
  });
  if (options.onFeaturesLoaded) {
    source.on('featuresloadend', options.onFeaturesLoaded);
  }
  const currentStyle = Carto.styles.find(
    (style) => couche.currentStyleId === style.id,
  );
  return new VectorLayer({
    source: source,
    style:
      currentStyle?.olStyle ??
      getDefaultStyle(index + options?.indexOffset ?? 0),
    opacity: couche.opacity || 1,
    visible: couche.visible,
    properties: {
      estCoucheMetier: true,
      titre: couche.titre,
      currentStyleId: couche.currentStyleId,
      stylesId: couche.stylesId,
      typeRecherche: couche.requete?.type,
      defaultStyle: getDefaultStyle(index + options?.indexOffset ?? 0),
      id: index, // Pas d'identifiant pour les couches/règles/conditions ... on doit se baser à l'index
    },
  });
}

function reloadFacettes(map, types, ids, maySimplify) {
  return loadFeatureForMap(map, types, ids, maySimplify, true);
}

async function loadFeatureForMap(
  map,
  types,
  ids,
  maySimplify = false,
  onlyReloadFacette = false,
) {
  let vectorLayer;
  map.getLayers().forEach((layer) => {
    if (layer.get('nom') === 'dossiersLayer') {
      vectorLayer = layer;
    }
  });
  let source = vectorLayer.getSource();
  if (source instanceof Cluster) {
    source = source.getSource();
  }

  const facettesData = {};
  const formFacette = document.querySelector('#carteFacettes form');
  if (formFacette != null) {
    new FormData(formFacette).forEach(
      (value, key) => (facettesData[key] = value),
    );
  }

  const extent = map.getView().calculateExtent();
  const url = addParamsToUrl(
    onlyReloadFacette ? Carto.rCartesFeaturesFacettes : Carto.rCartesFeatures,
    {
      bbox: extent.join(','),
      types: types ? types.join(',') : undefined,
      dossierIds: ids,
      maySimplify,
      ...facettesData,
    },
  );
  try {
    handleLoadingFacette(true);
    const response = await fetch(url);

    const json = await response.json();
    if (!json) {
      return;
    }
    const geoJson = json.geoJson;
    let features;
    if (!onlyReloadFacette && geoJson != null) {
      features = source.getFormat().readFeatures(geoJson);
      source.clear();
      source.addFeatures(features);
    }
    handleFacette(json.htmlFacette);

    return features;
  } catch {
    source.removeLoadedExtent(extent);
    return [];
  } finally {
    handleLoadingFacette(false);
  }
}

function handleLoadingFacette(loading) {
  const loadingDiv = document.querySelector(
    '.carte-resultats-facettes-chargement',
  );
  const divCarteFacettes = document.querySelector('#carteFacettes');
  if (loadingDiv != null) {
    if (loading) {
      divCarteFacettes.innerHTML = '';
      loadingDiv.classList.remove('non-visible');
    } else {
      loadingDiv.classList.add('non-visible');
    }
  }
}
function handleFacette(htmlFacette) {
  const divCarteFacettes = document.querySelector('#carteFacettes');
  if (divCarteFacettes != null) {
    divCarteFacettes.innerHTML = htmlFacette;
  }
}

function urlProxyfier(options) {
  return `${Carto.rCartesProxy}?url=${options.url}`;
}

function hashCode(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++)
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;

  return h;
}

export {
  reloadFacettes,
  initMapForDossier,
  initMapForRecherche,
  initMapForDossierIllustration,
  loadFeatureForMap,
};
